/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, Upload, Eye, EyeOff } from 'lucide-react';
import { adminBannersApi, productsApi } from '../../lib/api';
import { fileToBannerDataUrl } from '../../lib/imageUpload';

const EMPTY = {
  id: '',
  title: '',
  subtitle: '',
  image_desktop: '',
  image_tablet: '',
  image_mobile: '',
  link_type: 'none',
  link_value: '',
  open_in_new_tab: false,
  is_active: true,
  order: 0,
  placement: 'hero',
};

const CMS_PAGES = [
  { value: '/shop', label: 'Shop · All shades' },
  { value: '/quiz', label: 'Find Your Shade quiz' },
  { value: '/contact', label: 'Contact Us' },
  { value: '/shipping-policy', label: 'Shipping Policy' },
  { value: '/return-policy', label: 'Return & Refund Policy' },
  { value: '/privacy-policy', label: 'Privacy Policy' },
  { value: '/terms', label: 'Terms & Conditions' },
];

const CATEGORIES = [
  { value: 'matte', label: 'Matte' },
  { value: 'satin', label: 'Satin' },
  { value: 'sheer', label: 'Sheer' },
  { value: 'gloss', label: 'Gloss' },
];

const BannersTab = () => {
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      const [bs, ps] = await Promise.all([adminBannersApi.list(), productsApi.list()]);
      setBanners(bs);
      setProducts(ps);
    } catch (e) {
      setErr('Failed to load banners');
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (b) => setEditing({ ...b });
  const close = () => { setEditing(null); setErr(''); };

  const uploadInto = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const url = await fileToBannerDataUrl(file);
      setEditing((cur) => ({ ...cur, [field]: url }));
    } catch (ex) {
      setErr(ex.message || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing.image_desktop) { setErr('Desktop banner image is required'); return; }
    setBusy(true); setErr('');
    try {
      if (editing.id) await adminBannersApi.update(editing.id, editing);
      else await adminBannersApi.create(editing);
      close();
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    await adminBannersApi.remove(id);
    await load();
  };

  const toggleActive = async (b) => {
    await adminBannersApi.update(b.id, { ...b, is_active: !b.is_active });
    await load();
  };

  return (
    <div className="mt-8">
      <div className="flex justify-end mb-6">
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#1a1410] text-[#f5f1e6] px-6 py-3 text-[11px] tracking-[0.32em] hover:bg-black">
          <Plus className="w-4 h-4" strokeWidth={1.6} /> NEW BANNER
        </button>
      </div>

      {err && <div className="mb-4 p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}

      <div className="overflow-x-auto border border-[#e5dfd0]">
        <table className="w-full text-sm">
          <thead className="bg-[#ece4d2] text-left text-[10px] tracking-[0.2em] text-[#3a2e26]">
            <tr>
              <th className="p-4">PREVIEW</th>
              <th className="p-4">TITLE / LINK</th>
              <th className="p-4">PLACEMENT</th>
              <th className="p-4">ORDER</th>
              <th className="p-4">STATUS</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {banners.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-[#7a6a5a]">No banners yet — add one to populate the home page slider.</td></tr>
            ) : banners.map((b) => (
              <tr key={b.id} className="border-t border-[#e5dfd0] align-top">
                <td className="p-4">
                  <img src={b.image_desktop} alt={b.title} className="w-40 h-20 object-cover border border-[#cdc2b1]" />
                </td>
                <td className="p-4">
                  <div className="font-serif text-base">{b.title || <span className="text-[#7a6a5a] text-xs">(untitled)</span>}</div>
                  {b.subtitle && <div className="text-xs text-[#5a4a3e] mt-1">{b.subtitle}</div>}
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[#7a6a5a] mt-2">
                    {b.link_type === 'none' ? 'NO LINK' : `${b.link_type} → ${b.link_value || '—'}`}
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-[10px] tracking-[0.18em] uppercase px-2 py-1 bg-[#ece4d2] text-[#3a2e26]">
                    {b.placement || 'hero'}
                  </span>
                </td>
                <td className="p-4">{b.order}</td>
                <td className="p-4">
                  <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 ${b.is_active ? 'bg-[#dfecd9] text-[#3a6a3a]' : 'bg-[#cdc2b1] text-[#3a2e26]'}`}>
                    {b.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </td>
                <td className="p-4 whitespace-nowrap text-right">
                  <button onClick={() => toggleActive(b)} title={b.is_active ? 'Deactivate' : 'Activate'} className="p-1.5 hover:text-black">
                    {b.is_active ? <Eye className="w-3.5 h-3.5" strokeWidth={1.4} /> : <EyeOff className="w-3.5 h-3.5" strokeWidth={1.4} />}
                  </button>
                  <button onClick={() => openEdit(b)} title="Edit" className="p-1.5 hover:text-black"><Pencil className="w-3.5 h-3.5" strokeWidth={1.4} /></button>
                  <button onClick={() => remove(b.id)} title="Delete" className="p-1.5 text-[#7a3a2a] hover:text-[#5a1a0a]"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.4} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-[#f5f1e6] w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 h-16 border-b border-[#e5dfd0]">
              <div className="text-[11px] tracking-[0.28em]">{editing.id ? 'EDIT BANNER' : 'NEW BANNER'}</div>
              <button onClick={close}><X className="w-5 h-5" strokeWidth={1.4} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">TITLE (optional)</label>
                  <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">SUBTITLE (optional)</label>
                  <input value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
                </div>
              </div>

              <div>
                <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">PLACEMENT</label>
                <select value={editing.placement || 'hero'} onChange={(e) => setEditing({ ...editing, placement: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none">
                  <option value="hero">Hero slider (top of homepage)</option>
                  <option value="promo">Promotional banner (single, mid-homepage)</option>
                </select>
                <p className="text-[10px] text-[#7a6a5a] mt-1">
                  Promotional placement shows a single banner; only the first active one (lowest order) is displayed.
                </p>
              </div>

              {/* Image uploaders */}
              {[
                { key: 'image_desktop', label: 'DESKTOP IMAGE *', hint: 'Wide (e.g. 2400×900). Required.' },
                { key: 'image_tablet', label: 'TABLET IMAGE (optional)', hint: 'Falls back to desktop image if blank.' },
                { key: 'image_mobile', label: 'MOBILE IMAGE (optional)', hint: 'Portrait or square (e.g. 1080×1080). Falls back to desktop.' },
              ].map((f) => (
                <div key={f.key}>
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">{f.label}</label>
                    <label className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] cursor-pointer hover:text-black">
                      <Upload className="w-4 h-4" strokeWidth={1.4} /> UPLOAD
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadInto(e, f.key)} />
                    </label>
                  </div>
                  <p className="text-[10px] text-[#7a6a5a] mt-1">{f.hint}</p>
                  {editing[f.key] ? (
                    <div className="relative mt-3 border border-[#cdc2b1] overflow-hidden">
                      <img src={editing[f.key]} alt={f.label} className="w-full max-h-48 object-cover" />
                      <button type="button" onClick={() => setEditing({ ...editing, [f.key]: '' })}
                        className="absolute top-2 right-2 bg-[#1a1410] text-[#f5f1e6] p-1.5">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                      </button>
                    </div>
                  ) : (
                    <div className="mt-3 border border-dashed border-[#cdc2b1] py-8 text-center text-xs text-[#7a6a5a]">No image selected</div>
                  )}
                </div>
              ))}

              {/* Click action */}
              <div className="border border-[#e5dfd0] p-4 space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">CLICK ACTION</label>
                  <select value={editing.link_type} onChange={(e) => setEditing({ ...editing, link_type: e.target.value, link_value: '' })}
                    className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none">
                    <option value="none">No action — non-clickable</option>
                    <option value="product">Open a Product page</option>
                    <option value="collection">Open a Category / Collection page</option>
                    <option value="cms">Open a CMS / info page</option>
                    <option value="url">Open a custom URL</option>
                  </select>
                </div>

                {editing.link_type === 'product' && (
                  <div>
                    <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">SELECT PRODUCT</label>
                    <select value={editing.link_value} onChange={(e) => setEditing({ ...editing, link_value: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none">
                      <option value="">— select —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.shade})</option>)}
                    </select>
                  </div>
                )}

                {editing.link_type === 'collection' && (
                  <div>
                    <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">SELECT CATEGORY</label>
                    <select value={editing.link_value} onChange={(e) => setEditing({ ...editing, link_value: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none">
                      <option value="">— select —</option>
                      {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                )}

                {editing.link_type === 'cms' && (
                  <div>
                    <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">SELECT PAGE</label>
                    <select value={editing.link_value} onChange={(e) => setEditing({ ...editing, link_value: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none">
                      <option value="">— select —</option>
                      {CMS_PAGES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                )}

                {editing.link_type === 'url' && (
                  <>
                    <div>
                      <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">CUSTOM URL</label>
                      <input value={editing.link_value} onChange={(e) => setEditing({ ...editing, link_value: e.target.value })}
                        placeholder="https://example.com or /internal-path"
                        className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
                    </div>
                    <label className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em]">
                      <input type="checkbox" checked={!!editing.open_in_new_tab} onChange={(e) => setEditing({ ...editing, open_in_new_tab: e.target.checked })} />
                      OPEN IN NEW TAB
                    </label>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">DISPLAY ORDER</label>
                  <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value, 10) || 0 })}
                    className="mt-2 w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-2 focus:outline-none" />
                </div>
                <label className="inline-flex items-end gap-2 text-[11px] tracking-[0.22em]">
                  <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                  ACTIVE
                </label>
              </div>

              {err && <div className="p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={busy} className="flex-1 bg-[#1a1410] text-[#f5f1e6] py-3 text-[11px] tracking-[0.32em] hover:bg-black disabled:opacity-50">
                  {busy ? 'SAVING…' : 'SAVE BANNER'}
                </button>
                <button type="button" onClick={close} className="px-6 py-3 text-[11px] tracking-[0.32em] border border-[#cdc2b1]">CANCEL</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannersTab;
